import * as React from 'react';
import { useState, useCallback, useMemo } from 'react';
import styled, { css } from 'styled-components';
import { Formik, Form, Field, useFormikContext } from 'formik';

import { Button, Badge, Alert } from 'components/bootstrap';
import { Icon, ModalSubmit } from 'components/common';
import Popover from 'components/common/Popover';
import type { FieldFilters, FieldsOperator } from 'data-lake/Types';
import FieldFiltersConfiguration from 'data-lake/FieldFiltersConfiguration/FieldFiltersConfiguration';
import TypeSpecificValue from 'views/components/TypeSpecificValue';
import FieldType from 'views/logic/fieldtypes/FieldType';
import useFieldTypes from 'data-lake/hooks/useFieldTypes';
import OperatorField from 'data-lake/preview/OperatorField';
import { TELEMETRY_EVENT_TYPE } from 'telemetry/Constants';
import useSendTelemetry from 'logic/telemetry/useSendTelemetry';
import useCurrentDataLakePlugin from 'data-lake/preview/hooks/useCurrentDataLakePlugin';
import { DocumentationLink } from 'components/support';
import DocsHelper from 'util/DocsHelper';
import validateFieldFilters from 'data-lake/logic/validateFieldFilters';
import useSelectedDataLake from 'data-lake/hooks/useSelectedDataLake';

const Container = styled.div(
  ({ theme }) => css`
    display: flex;
    gap: ${theme.spacings.sm};
  `,
);

type QueryBuilderFormValues = {
  fieldFilters: Array<FieldFilters>;
  operator: FieldsOperator;
};

const QueryString = styled.div(
  ({ theme }) => css`
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: ${theme.spacings.xs};
    min-height: 34px;
  `,
);

const NoFiltersInfo = styled(Alert)`
  margin-top: 0;
`;

const validateQueryBuilderForm = ({
  fieldFilters,
  operator,
  inMemoryFields,
}: QueryBuilderFormValues & { inMemoryFields: Set<string> }) => {
  const errors: { fieldFilters?: Array<{ field_name?: string; value?: string }> } = {};

  const fieldFiltersErrors = validateFieldFilters(fieldFilters, operator, inMemoryFields);

  if (fieldFiltersErrors.length > 0) {
    errors.fieldFilters = fieldFiltersErrors;
  }

  return errors;
};

const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const SearchBarFieldsFilter = () => {
  const [showBuilder, setShowBuilder] = useState(false);
  const { values: searchBarValues } = useFormikContext();
  const { extraValidations } = useCurrentDataLakePlugin();
  const toggleBuilder = () => setShowBuilder((cur) => !cur);
  const { data: fieldTypes, inMemoryFields } = useFieldTypes();
  const { selectedDataLake } = useSelectedDataLake();
  const sendTelemetry = useSendTelemetry();

  const validation = useCallback(
    ({ fieldFilters, operator }: QueryBuilderFormValues) =>
      validateQueryBuilderForm({ fieldFilters, operator, inMemoryFields }),
    [inMemoryFields],
  );

  const fieldsValidationError = useMemo(() => {
    if (typeof extraValidations?.fieldFilters === 'function') return extraValidations.fieldFilters(searchBarValues);

    return null;
  }, [extraValidations, searchBarValues]);

  return (
    <Field name="fields">
      {({ field: { name, value, onChange } }) => {
        const changeValue = (newValue: { fieldFilters: Array<FieldFilters>; operator: FieldsOperator }) => {
          onChange({
            target: {
              value: newValue,
              name,
            },
          });
        };

        const onClearFieldFilters = () => {
          sendTelemetry(TELEMETRY_EVENT_TYPE.DATALAKE.PREVIEW[selectedDataLake.type].CLEAR_FIELD_FILTERS, {
            app_pathname: '/data-storage/preview',
            app_section: 'search-bar',
            app_action_value: 'clear-field-filters',
            event_details: {
              data_lake_storage_type: selectedDataLake.storageType,
            },
          });
          changeValue({ fieldFilters: [], operator: 'AND' });
        };

        const onSubmitBuilder = (newValue: { fieldFilters: Array<FieldFilters>; operator: FieldsOperator }) => {
          sendTelemetry(TELEMETRY_EVENT_TYPE.DATALAKE.PREVIEW[selectedDataLake.type].SUBMIT_FIELD_FILTERS_FORM, {
            app_pathname: '/data-storage/preview',
            app_section: 'search-bar',
            app_action_value: 'update-field-filters',
            eventDetails: {
              fieldFilters: newValue?.fieldFilters?.map(({ field_name: fieldName }) => fieldName),
              operator: newValue?.operator,
              data_lake_storage_type: selectedDataLake.storageType,
            },
          });

          changeValue(newValue);

          toggleBuilder();
        };

        return (
          <Container>
            <Popover
              position="bottom-start"
              width={500}
              opened={showBuilder}
              withArrow
              onChange={setShowBuilder}
              closeOnClickOutside
              withinPortal>
              <Popover.Target>
                <Button
                  onClick={toggleBuilder}
                  disabled={!!fieldsValidationError}
                  title={fieldsValidationError ?? 'Configure field filter'}>
                  Filter by field <Icon name="arrow_drop_down" />
                </Button>
              </Popover.Target>

              {value?.fieldFilters?.length >= 1 && (
                <QueryString>
                  {value.fieldFilters.map(({ field_name: fieldName, value: fieldValue }, index) => (
                    // eslint-disable-next-line react/no-array-index-key
                    <React.Fragment key={index}>
                      {index !== 0 && value.operator}
                      <Badge bsStyle="info">
                        {fieldName}:
                        <TypeSpecificValue
                          value={fieldValue}
                          field={fieldName}
                          type={(fieldTypes?.find((f) => f.name === fieldName) || { type: FieldType.Unknown }).type}
                        />
                      </Badge>
                    </React.Fragment>
                  ))}
                  <Button onClick={onClearFieldFilters} title="Clear field filters">
                    Clear
                  </Button>
                </QueryString>
              )}

              <Popover.Dropdown
                title={
                  <Header>
                    <span>Build Field Filter</span>
                    <DocumentationLink page={DocsHelper.PAGES.DATA_LAKE} displayIcon text="" />
                  </Header>
                }
                id="build-fields-filter-dropdown">
                <Formik<{ fieldFilters: Array<FieldFilters> }>
                  initialValues={
                    value?.fieldFilters?.length > 0
                      ? value
                      : { fieldFilters: [{ field_name: '', value: '' }], operator: 'AND' }
                  }
                  onSubmit={onSubmitBuilder}
                  validate={validation}>
                  {({ isValid, isSubmitting, values }) => (
                    <Form>
                      <OperatorField />
                      {values?.fieldFilters?.length < 1 && (
                        <NoFiltersInfo>All field filters have been removed.</NoFiltersInfo>
                      )}
                      <FieldFiltersConfiguration />
                      <ModalSubmit
                        submitButtonText="Update filter"
                        submitLoadingText="Updateting filter..."
                        disabledSubmit={!isValid || isSubmitting}
                        onCancel={toggleBuilder}
                      />
                    </Form>
                  )}
                </Formik>
              </Popover.Dropdown>
            </Popover>
          </Container>
        );
      }}
    </Field>
  );
};

export default SearchBarFieldsFilter;
